import React, { useState } from 'react';
import {
    Card, CardContent, CardActions, Typography, IconButton,
    Chip, Box, Tooltip, Collapse, Link
} from '@mui/material';
import {
    Delete as DeleteIcon, Place as PlaceIcon, Language as LanguageIcon,
    ExpandMore as ExpandMoreIcon, ExpandLess as ExpandLessIcon,
    Public as PublicIcon, OpenInNew as OpenInNewIcon,
    Visibility as VisibilityIcon
} from '@mui/icons-material';

const SeenPlaceCard = ({
    seenEntry,
    onDelete,
    onMapFocus
}) => {
    const [expanded, setExpanded] = useState(false);

    const getLanguageDisplay = () => {
        if (seenEntry.language.customLabel && seenEntry.language.customLanguageCode) {
            return `${seenEntry.language.customLabel} (${seenEntry.language.customLanguageCode})`;
        }
        if (seenEntry.language.wikidataLabel) {
            const nativeLabel = seenEntry.language.nativeLabels ? ` (${seenEntry.language.nativeLabels})` : '';
            return `${seenEntry.language.wikidataLabel}${nativeLabel}`;
        }
        return 'Unknown Language';
    };

    const getWikidataUrl = () => {
        if (!seenEntry.place.wikidataID) return null;
        const qId = seenEntry.place.wikidataID.replace('http://www.wikidata.org/entity/', '');
        return `https://www.wikidata.org/wiki/${qId}`;
    };

    const getWikidataId = () => {
        if (!seenEntry.place.wikidataID) return null;
        return seenEntry.place.wikidataID.replace('http://www.wikidata.org/entity/', '');
    };

    const formatTimestamp = () => {
        if (!seenEntry.timestamp) return '';
        return new Date(seenEntry.timestamp).toLocaleDateString();
    };

    return (
        <Card 
            elevation={1} 
            sx={{ 
                mb: 1.5, 
                border: '1px solid #e5e7eb',
                backgroundColor: '#f9fafb',
                transition: 'all 0.2s ease',
                '&:hover': { 
                    boxShadow: '0 4px 8px -2px rgba(0, 0, 0, 0.1)',
                    transform: 'translateY(-1px)'
                }
            }}
        >
            <CardContent sx={{ pb: 1 }}>
                {/* Header with place name and seen indicator */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                    <Box sx={{ flex: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, gap: 1 }}>
                            <VisibilityIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
                            <Typography variant="body1" component="h4" sx={{ fontWeight: 600, flex: 1 }}>
                                {seenEntry.place.wikidataLabel || 'Unnamed Place'}
                            </Typography>
                            {getWikidataUrl() && (
                                <Tooltip title={`View ${getWikidataId()} on Wikidata`}>
                                    <Link
                                        href={getWikidataUrl()}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        sx={{
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            gap: 0.5,
                                            fontSize: '0.7rem',
                                            fontWeight: 500,
                                            textDecoration: 'none',
                                            color: 'text.secondary',
                                            padding: '2px 6px',
                                            borderRadius: 1,
                                            border: '1px solid #d1d5db',
                                            transition: 'all 0.2s ease',
                                            '&:hover': {
                                                backgroundColor: 'text.secondary',
                                                color: 'white'
                                            }
                                        }}
                                    >
                                        {getWikidataId()}
                                        <OpenInNewIcon sx={{ fontSize: 10 }} />
                                    </Link>
                                </Tooltip>
                            )}
                        </Box>
                        
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Chip 
                                icon={<LanguageIcon />}
                                label={getLanguageDisplay()}
                                size="small"
                                variant="outlined"
                                sx={{ 
                                    borderColor: 'text.secondary',
                                    color: 'text.secondary',
                                    fontSize: '0.7rem'
                                }}
                            />
                            {formatTimestamp() && (
                                <Typography variant="caption" color="text.secondary">
                                    Marked on {formatTimestamp()}
                                </Typography>
                            )}
                        </Box>
                    </Box>
                </Box>

                {/* Collapsible details */}
                <Collapse in={expanded}>
                    <Box sx={{ mt: 2, p: 1.5, bgcolor: 'grey.100', borderRadius: 1 }}>
                        <Typography variant="body2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                            <PublicIcon sx={{ mr: 1, fontSize: 16 }} />
                            <strong>Wikidata ID:</strong> 
                            <Typography component="span" sx={{ ml: 1, fontFamily: 'monospace' }}>
                                {getWikidataId()}
                            </Typography>
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            <strong>Coordinates:</strong> {seenEntry.place.lat.toFixed(6)}, {seenEntry.place.lon.toFixed(6)}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1, fontStyle: 'italic' }}>
                            This place was marked as seen, indicating it was edited directly on Wikidata.
                        </Typography>
                    </Box>
                </Collapse>
            </CardContent>

            <CardActions sx={{ justifyContent: 'space-between', pt: 0, pb: 1 }}>
                <Box>
                    <Tooltip title="Focus on map">
                        <IconButton 
                            onClick={() => onMapFocus(seenEntry.place)} 
                            color="info" 
                            size="small"
                        >
                            <PlaceIcon />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="Remove from seen list">
                        <IconButton 
                            onClick={() => onDelete(seenEntry)} 
                            color="error" 
                            size="small"
                        >
                            <DeleteIcon />
                        </IconButton>
                    </Tooltip>
                </Box>

                <Box>
                    <Tooltip title={expanded ? "Show less details" : "Show more details"}>
                        <IconButton 
                            onClick={() => setExpanded(!expanded)} 
                            size="small"
                        >
                            {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                        </IconButton>
                    </Tooltip>
                </Box>
            </CardActions>
        </Card>
    );
};

export default SeenPlaceCard;